/**
 * findingsClipboard: the ONE formatter behind every "Copy findings" item.
 *
 * Why it exists (board row 2026-09-13 P0): the four findings menus offered
 * exactly two destinations -- Create Issues (stays inside OverlayQA) and
 * "Export to {provider}" (needs an OAuth handshake the day-one evaluator has
 * not done). A solo evaluator could produce 20 findings and had no way to
 * hand them to a developer as text. Emily's call 2026-09-13: all three
 * formats, "all including just copy findings".
 *
 * Single-sourced here because FOUR surfaces render the same two menus
 * (A11yAuditPanel, CompareResults and AutoReviewPanel share
 * A11yBulkActionsMenu; TokenAuditPanel has the mirrored
 * TokenAuditBulkActionsMenu). A per-surface copy of this logic is exactly how
 * the avatar-initials drift happened, so each panel supplies only the
 * normalisation of its own finding shape and the rendering lives here.
 */
/** Blank and absent are the same thing here: neither earns a label. */
function present(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
/**
 * Newlines are structural in all three formats: they end a numbered line, a
 * markdown list item, and (unquoted) a CSV record. Every value is collapsed to
 * one line before rendering; CSV re-admits real newlines only inside quotes.
 */
function collapse(value) {
    return value.replace(/\s+/g, ' ').trim();
}
/** The detail labels present across the set, in first-seen order. */
function detailLabels(findings) {
    const seen = [];
    for (const finding of findings) {
        for (const detail of finding.details ?? []) {
            if (present(detail.value) && !seen.includes(detail.label))
                seen.push(detail.label);
        }
    }
    return seen;
}
function detailValue(finding, label) {
    const hit = (finding.details ?? []).find((d) => d.label === label);
    return hit && present(hit.value) ? hit.value : '';
}
// ─── text ───────────────────────────────────────────────────────────────────
function renderText(findings, context) {
    const lines = [`${context.heading} (${findings.length})`];
    if (present(context.pageUrl))
        lines.push(context.pageUrl);
    lines.push('');
    findings.forEach((finding, index) => {
        const severity = present(finding.severity) ? ` [${collapse(finding.severity)}]` : '';
        lines.push(`${index + 1}. ${collapse(finding.title)}${severity}`);
        if (present(finding.issueType))
            lines.push(`   Issue type: ${collapse(finding.issueType)}`);
        if (present(finding.pageUrl))
            lines.push(`   Page: ${collapse(finding.pageUrl)}`);
        if (present(finding.description))
            lines.push(`   ${collapse(finding.description)}`);
        if (present(finding.selector))
            lines.push(`   Selector: ${collapse(finding.selector)}`);
        if (present(finding.screenshotUrl))
            lines.push(`   Screenshot: ${collapse(finding.screenshotUrl)}`);
        for (const detail of finding.details ?? []) {
            if (present(detail.value))
                lines.push(`   ${detail.label}: ${collapse(detail.value)}`);
        }
        lines.push('');
    });
    return lines.join('\n').trimEnd() + '\n';
}
// ─── markdown ───────────────────────────────────────────────────────────────
/** Escapes the characters that change how a PLAIN value renders. Also the issue-copy title escape (server lib/issueCopyText.ts). */
export function escapeMarkdownText(value) {
    return mdEscape(value);
}
function mdEscape(value) {
    return collapse(value)
        .replace(/\\/g, '\\\\')
        .replace(/([|*_[\]])/g, '\\$1');
}
/**
 * A code span cannot carry a raw backtick without ending itself, and the
 * CommonMark remedy (a longer fence) still leaves the single-fenced substring
 * inside the output. Neutralising the character is the only form that cannot
 * break the surrounding document; a CSS selector containing a literal backtick
 * is vanishingly rare and a broken fence is not.
 */
function mdCode(value) {
    return '`' + collapse(value).replace(/`/g, "'") + '`';
}
/**
 * A value carrying markup goes in a code span rather than through entity
 * escaping. Both render correctly, but `&lt;button&gt;` is what a reader sees
 * wherever the markdown is NOT rendered (a plain-text box, a terminal, a diff),
 * and a11y's "Found" row is literal HTML on almost every finding. Read off the
 * real rendered artifact 2026-09-14, not from the assertions.
 */
function mdValue(value) {
    return /[<>]/.test(value) ? mdCode(value) : mdEscape(value);
}
/**
 * An IMAGE, never a bare URL: a screenshot that renders inline is the whole
 * reason the field exists, and every markdown target renders a bare link as
 * text. The URL is NOT escaped (it is a real URL, and mdEscape would break the
 * underscores and brackets an upload path can carry); a destination carrying a
 * space or a paren is wrapped in <> per CommonMark instead, which is the only
 * form that cannot end the link early.
 */
function mdImage(alt, url) {
    const dest = /[\s()]/.test(url) ? `<${url.replace(/[<>]/g, '')}>` : url;
    return `![${mdEscape(alt)}](${dest})`;
}
function renderMarkdown(findings, context) {
    const lines = [`## ${mdEscape(context.heading)} (${findings.length})`, ''];
    if (present(context.pageUrl)) {
        lines.push(`Page: ${mdCode(context.pageUrl)}`, '');
    }
    findings.forEach((finding, index) => {
        lines.push(`### ${index + 1}. ${mdEscape(finding.title)}`, '');
        if (present(finding.issueType))
            lines.push(`- **Issue type:** ${mdEscape(finding.issueType)}`);
        if (present(finding.severity))
            lines.push(`- **Severity:** ${mdEscape(finding.severity)}`);
        if (present(finding.pageUrl))
            lines.push(`- **Page:** ${mdCode(finding.pageUrl)}`);
        if (present(finding.selector))
            lines.push(`- **Selector:** ${mdCode(finding.selector)}`);
        if (present(finding.screenshotUrl)) {
            lines.push(`- **Screenshot:** ${mdImage(finding.title, finding.screenshotUrl)}`);
        }
        if (present(finding.description)) {
            lines.push(`- **Description:** ${mdValue(finding.description)}`);
        }
        for (const detail of finding.details ?? []) {
            if (present(detail.value)) {
                lines.push(`- **${mdEscape(detail.label)}:** ${detail.image ? mdImage(detail.label, detail.value) : mdValue(detail.value)}`);
            }
        }
        lines.push('');
    });
    return lines.join('\n').trimEnd() + '\n';
}
// ─── csv ────────────────────────────────────────────────────────────────────
/**
 * Excel, Sheets and Numbers execute a cell beginning = + - @ (or a leading tab
 * / CR) as a formula. Every cell here is arbitrary text lifted off the scanned
 * PAGE, so a hostile page can plant one and it runs on the auditor's machine
 * when they paste. OWASP CSV injection; the documented remedy is a leading
 * apostrophe, which spreadsheets strip on display.
 */
function csvDefuse(value) {
    // A plain signed number is a NUMBER to a spreadsheet, never a formula, and
    // prefixing it turns a sortable, summable cell into text plus a stray
    // apostrophe for every non-spreadsheet reader. Design token findings report
    // negative values routinely ("-8"), so exempt them; "-4px" is not a number
    // and Excel does try to evaluate it, so it stays defused.
    if (/^[+-]?\d+(\.\d+)?$/.test(value))
        return value;
    return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}
/** RFC 4180: quote when the cell carries a comma, a quote or a newline. */
function csvCell(value) {
    if (!present(value))
        return '';
    const safe = csvDefuse(value);
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}
function renderCsv(findings, context) {
    const labels = detailLabels(findings);
    const header = ['Title', 'Issue type', 'Severity', 'Selector', 'Screenshot', 'Description', 'Page URL', ...labels];
    const rows = findings.map((finding) => [
        finding.title,
        finding.issueType ?? '',
        finding.severity ?? '',
        finding.selector ?? '',
        finding.screenshotUrl ?? '',
        finding.description ?? '',
        present(finding.pageUrl) ? finding.pageUrl : (context.pageUrl ?? ''),
        ...labels.map((label) => detailValue(finding, label)),
    ]
        .map(csvCell)
        .join(','));
    return [header.map(csvCell).join(','), ...rows].join('\n') + '\n';
}
// ─── entry point ────────────────────────────────────────────────────────────
/**
 * Renders a selected findings set for the clipboard. An empty selection
 * returns an empty string so the caller copies nothing rather than an
 * artifact that says nothing.
 */
export function formatFindingsForClipboard(findings, format, context) {
    if (findings.length === 0)
        return '';
    if (format === 'markdown')
        return renderMarkdown(findings, context);
    if (format === 'csv')
        return renderCsv(findings, context);
    return renderText(findings, context);
}
//# sourceMappingURL=findingsClipboard.js.map